// Журнал действий (тз, разделы 3.1 и 8.5).
//
// ## Почему экран появился только сейчас
//
// Записи в журнал пишутся с первого дня и не редактируются — это сценарий приёмки 67, и он
// закрыт. Прочитать журнал было нечем: тз обещает администратору «доступ к журналу
// действий», а доступ существовал только запросом в базу руками. Право `core.audit.view`
// было выдано трём ролям и не вело никуда.
//
// ## Почему по умолчанию показываются не все записи
//
// Замер на стенде: 3743 записи из 3826 — это входы в систему. Открытый «как есть» журнал
// показывает стену одинаковых строк «вошёл в систему», а единственное, ради чего в него
// зашли — кто изменил тариф, — лежит страниц через двадцать. Поэтому по умолчанию стоит
// отбор «все действия, кроме входов», и он ВИДЕН человеку в поле отбора: это не скрытие,
// а выбранное умолчание, которое одним нажатием снимается.
//
// ## Почему листается номером, а не страницами
//
// Журнал дописывается непрерывно, в том числе прямо во время чтения. При листании
// смещением каждая новая запись сдвигает всё вниз: человек видит одну строку дважды, а
// какую-то не видит вовсе. Номер записи плотный и возрастающий, и «дай то, что старше
// номера N» отвечает одинаково, сколько бы ни дописали сверху.
//
// ## Отмены и списания (14.10.2026)
//
// Заказчик письменно отдал администратору отмену проведённых денег и списание долга при одном
// условии: журнал показывает, кто и когда это сделал, КАКУЮ запись отменил, на какую сумму и по
// какому основанию, а исходные записи не исчезают. Отсюда три вещи на этом экране: отбор
// «Отмены и списания» (директор открывает журнал с одним вопросом и не листает правки), строка
// «что именно» под действием («Выдача 12 000 ₽ · Вихрев Сергей · 12.10.2026») и дата с годом и
// временем с секундами — запись, которую показывают как доказательство, обязана называть
// точный момент.

import { RiHistoryLine } from '@remixicon/react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router';

import { fetchAudit, type AuditEntry } from '@/shared/api/endpoints/audit';
import { listUsers } from '@/shared/api/endpoints/users';
import { useAuthStore } from '@/shared/auth/store';
import type { ApiError } from '@/shared/errors/api-error';
import { can } from '@/shared/rbac/permissions';
import type { SearchState } from '@/shared/routing/search';
import { buildSearch } from '@/shared/routing/search';
import { countLabel } from '@/shared/text/plural';
import { formatDataAsOf, parseMoment } from '@/shared/time/format';
import * as Button from '@/shared/ui/button';
import { EmptyState, ScreenState, Skeleton } from '@/shared/ui/screen-state';
import { NO_VALUE, SelectField } from '@/shared/ui/select-field';
import { Toolbar } from '@/shared/ui/toolbar';

import {
  ГРУППА_ОТМЕНЫ,
  ГРУППЫ,
  СУЩНОСТИ_КРОМЕ_ВХОДОВ,
  значениеПоЧеловечески,
  названиеПоля,
  чтоПроизошло,
} from './словарь';

/** Отбор группы в адресе: `?f.what=деньги`. */
export const FILTER_GROUP = 'what';
/** Отбор по человеку: `?f.who=<id>`. */
export const FILTER_WHO = 'who';

/** Сколько записей за раз. Сто строк — примерно три экрана прокрутки. */
const СТРАНИЦА = 100;

/** «Все действия, кроме входов» — умолчание, названное словами в самом отборе. */
const БЕЗ_ВХОДОВ = 'дела';

/**
 * Что сказать, когда за отбором записей нет. Причин три, и фраза у каждой своя: «пока никто не
 * отменял» неверно для финотдела (он видит только свои действия) и для отбора по одному человеку
 * (тот мог не отменять, а другие отменяли).
 */
function пустоеСостояние(
  группа: string,
  кто: string | undefined,
  виденВесьЖурнал: boolean,
): { заголовок: string; описание: string } {
  if (группа !== ГРУППА_ОТМЕНЫ) {
    return {
      заголовок: 'За этим отбором записей нет',
      описание:
        'Журнал пишется самими действиями и не правится ничем. Пусто — значит таких действий не было: смените отбор.',
    };
  }
  if (!виденВесьЖурнал) {
    return {
      заголовок: 'В вашем журнале отмен и списаний нет',
      описание:
        'Здесь ваши собственные действия. Отмены и списания делают руководство и администратор.',
    };
  }
  if (кто !== undefined) {
    return {
      заголовок: 'У этого человека отмен и списаний не было',
      описание: 'Выберите другого человека или снимите отбор «Кто»: у остальных они могли быть.',
    };
  }
  // Здесь пусто — это хорошая новость, а не поломка: отмен не было. И сказать надо, что будет
  // видно, когда они появятся: ради этого экрана отбор и заведён.
  return {
    заголовок: 'Отмен и списаний не было',
    описание:
      'Пока никто не отменял проведённое и не списывал долги. Когда это случится, здесь будет видно, кто, когда, что и на какую сумму.',
  };
}

export function AuditView({ search }: { search: SearchState }) {
  const навигация = useNavigate();
  const адрес = useLocation();
  const права = useAuthStore((состояние) => состояние.permissions);
  const виденВесьЖурнал = can(права, 'core.audit.view');

  const группа = search.filters[FILTER_GROUP] ?? БЕЗ_ВХОДОВ;
  const кто = search.filters[FILTER_WHO];

  const выбранная =
    группа === БЕЗ_ВХОДОВ ? undefined : ГРУППЫ.find((строка) => строка.код === группа);
  // Группа по действию («Отмены и списания») сущности не отбирает вовсе: отмена у каждого вида
  // денег своя, а слово одно. Остальные группы отбирают по сущности, как и раньше.
  const действия = выбранная?.действия;
  const сущности =
    действия !== undefined ? undefined : (выбранная?.сущности ?? СУЩНОСТИ_КРОМЕ_ВХОДОВ);

  // Листание курсором: следующая страница — «то, что старше последней показанной записи».
  // Смещения здесь нет намеренно, см. пояснение вверху файла.
  const журнал = useInfiniteQuery({
    queryKey: ['audit', группа, кто ?? null],
    initialPageParam: null as number | null,
    queryFn: ({ pageParam }) =>
      fetchAudit({
        entity: сущности,
        action: действия,
        user_id: кто,
        before_id: pageParam ?? undefined,
        limit: СТРАНИЦА,
      }),
    getNextPageParam: (последняя) =>
      последняя.items.length < СТРАНИЦА
        ? undefined
        : (последняя.items[последняя.items.length - 1]?.id ?? undefined),
  });

  // Список людей нужен только тому, кто видит весь журнал: у финансового отдела в журнале
  // и так одни его собственные записи, и отбор «кто» предлагал бы выбрать из одного себя.
  const люди = useQuery({
    queryKey: ['audit', 'люди'],
    queryFn: () => listUsers({ active_only: false }),
    enabled: виденВесьЖурнал,
  });

  function сменитьОтбор(ключ: string, значение: string | null) {
    const фильтры = { ...search.filters };
    if (значение === null) {
      delete фильтры[ключ];
    } else {
      фильтры[ключ] = значение;
    }
    void навигация({
      pathname: адрес.pathname,
      search: buildSearch({ ...search, filters: фильтры, panels: [] }),
    });
  }

  const пустое = пустоеСостояние(группа, кто, виденВесьЖурнал);

  const страницы = журнал.data?.pages ?? [];
  const строки = страницы.flatMap((страница) => страница.items);
  const всего = страницы[0]?.total ?? 0;
  const срез = страницы[0] === undefined ? null : parseMoment(страницы[0].generated_at);

  return (
    <main className='flex min-h-0 min-w-0 flex-1 flex-col bg-bg-white-0'>
      <Toolbar>
        <div className='min-w-64'>
          <SelectField
            id='audit-group'
            label='Что показывать'
            value={группа}
            placeholder='все действия, кроме входов'
            options={[
              { value: БЕЗ_ВХОДОВ, label: 'все действия, кроме входов' },
              // «Отмены и списания» делают руководство и администратор, и видят их тоже они: тому,
              // у кого журнал только свой (финотдел), отбор предложил бы заведомо пустой экран.
              ...ГРУППЫ
                .filter((строка) => строка.код !== ГРУППА_ОТМЕНЫ || виденВесьЖурнал)
                .map((строка) => ({ value: строка.код, label: строка.название })),
            ]}
            onChange={(значение) => сменитьОтбор(FILTER_GROUP, значение)}
          />
        </div>
        {виденВесьЖурнал && (
          <div className='min-w-56'>
            <SelectField
              id='audit-who'
              label='Кто'
              value={кто ?? NO_VALUE}
              options={(люди.data?.items ?? []).map((строка) => ({
                value: строка.id,
                label: строка.display_name,
              }))}
              noneLabel='все люди'
              placeholder='все люди'
              pending={люди.isPending}
              failed={люди.isError}
              failedError={люди.error as ApiError | null}
              onChange={(значение) =>
                сменитьОтбор(FILTER_WHO, значение === NO_VALUE ? null : значение)
              }
            />
          </div>
        )}
        {!виденВесьЖурнал && (
          // Финансовый отдел видит только свои записи, и молчать об этом нельзя: человек
          // решит, что в системе за неделю ничего не происходило.
          <p className='max-w-prose pb-2 text-paragraph-sm text-text-sub-600'>
            Здесь ваши собственные действия. Журнал целиком открыт администратору и руководству.
          </p>
        )}
      </Toolbar>

      <ScreenState
        data={журнал.data}
        error={журнал.error as ApiError | null}
        subject='журнал действий'
        isEmpty={() => строки.length === 0}
        onRetry={() => void журнал.refetch()}
        loading={
          <div className='flex flex-col gap-2 p-5'>
            <Skeleton className='h-14 w-full' />
            <Skeleton className='h-14 w-full' />
            <Skeleton className='h-14 w-full' />
          </div>
        }
        empty={
          <div className='p-5'>
            <EmptyState
              icon={RiHistoryLine}
              title={пустое.заголовок}
              illustration='заметки'
              description={пустое.описание}
            />
          </div>
        }
      >
        {() => (
          <div className='flex min-h-0 flex-1 flex-col'>
            <div className='flex items-baseline justify-between gap-4 px-5 pb-2 pt-3'>
              <span className='text-paragraph-sm text-text-sub-600'>
                {countLabel(всего, ['запись', 'записи', 'записей'])}
                {строки.length < всего && ` · показано ${строки.length}`}
              </span>
              {срез !== null && (
                <span className='shrink-0 text-paragraph-xs text-text-sub-600'>
                  {formatDataAsOf(срез)}
                </span>
              )}
            </div>

            <ul className='min-h-0 flex-1 divide-y divide-stroke-soft-200 overflow-y-auto border-t border-stroke-soft-200'>
              {строки.map((запись) => (
                <li key={запись.id} className='flex items-baseline gap-4 px-5 py-3'>
                  {/* Время слева и одной ширины у всех строк: журнал читают сверху вниз по
                      колонке времени, а не по содержанию.
                      Дата и часы РАЗНЫМИ строками, а не «03.09, 02:13» одной: слитная запись
                      заводит третью грамматику чисел в продукте, где их две — дата и время.
                      Сторож грамматики её и поймал. */}
                  <span className='flex w-20 shrink-0 flex-col tabular-nums text-paragraph-xs text-text-sub-600'>
                    <span>{датаСГодом(запись.at)}</span>
                    <span>{времяССекундами(запись.at)}</span>
                  </span>
                  <span className='w-40 shrink-0 truncate text-label-sm text-text-strong-950'>
                    {запись.user_name ?? 'система'}
                  </span>
                  <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
                    {/* Когда известно, ЧТО именно тронули, главное на строке — это оно, а
                        действие уходит на второй план: «сторнировал · выдача денег» без суммы
                        и человека не отвечает на вопрос, ради которого журнал открывают. */}
                    <span
                      className={
                        запись.subject
                          ? 'text-paragraph-xs text-text-sub-600'
                          : 'text-paragraph-sm text-text-strong-950'
                      }
                    >
                      {чтоПроизошло(запись.action, запись.entity)}
                    </span>
                    {запись.subject ? (
                      <span className='break-words text-paragraph-sm text-text-strong-950'>
                        {запись.subject}
                      </span>
                    ) : null}
                    <Изменение запись={запись} />
                    {/* Основание пишет человек («удержано при выплате 11.10»), и его числа не
                        наша грамматика: пометка говорит об этом сквозной проверке чисел. */}
                    {запись.basis !== null && запись.basis !== undefined && (
                      <span
                        data-текст='от человека'
                        className='text-paragraph-xs text-text-sub-600'
                      >
                        Основание: {запись.basis}
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>

            {журнал.hasNextPage && (
              // Поле у подвала то же, что у шапки и строк: 20, в узком окне 16. Пока здесь стояло
              // `p-3`, содержимое подвала начиналось на 8 точек левее остального, и сквозная
              // проверка «полосы одной колонки начинают содержимое одинаково» краснела на журнале
              // (замер 01.10.2026). Кнопка стоит по центру, и глазом этого не видно: левый край у
              // полос общий, и держится он полем, а не выравниванием.
              <div className='shrink-0 border-t border-stroke-soft-200 px-5 py-3 text-center узко:px-4'>
                <Button.Root
                  variant='neutral'
                  mode='stroke'
                  size='small'
                  disabled={журнал.isFetchingNextPage}
                  onClick={() => void журнал.fetchNextPage()}
                >
                  {журнал.isFetchingNextPage ? 'Читаем журнал…' : 'Показать ещё'}
                </Button.Root>
              </div>
            )}
          </div>
        )}
      </ScreenState>
    </main>
  );
}

/**
 * Что именно изменилось: «роли: охранник → табельщик».
 *
 * Имя поля показывается, только если для него есть русское слово. Служебное имя колонки
 * рядом с парой значений не помогает, а мешает: человек читает его как часть значения.
 */
function Изменение({ запись }: { запись: AuditEntry }) {
  const было = запись.old_value;
  const стало = запись.new_value;
  if ((было === null || было === undefined) && (стало === null || стало === undefined)) {
    return null;
  }
  const поле = названиеПоля(запись.field);
  const словами = (значение: string) => значениеПоЧеловечески(значение, запись);
  return (
    <span className='text-paragraph-xs text-text-sub-600'>
      {поле !== null && <span>{поле}: </span>}
      {было !== null && было !== undefined && было !== '' ? (
        <>
          <span className='line-through'>{словами(было)}</span>
          {' → '}
        </>
      ) : null}
      <span className='text-text-strong-950'>
        {стало === null || стало === undefined ? 'пусто' : словами(стало)}
      </span>
    </span>
  );
}

/**
 * «18.10.2026». С годом: до 14.10.2026 журнал читали за последние дни и год был лишним, а теперь
 * его показывают как доказательство отмены денег, и запись без года читается как неполная.
 */
function датаСГодом(iso: string): string {
  const момент = parseMoment(iso);
  return момент === null
    ? ''
    : момент.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** «14:32:05». С секундами: две отмены за одну минуту различаются только ими. */
function времяССекундами(iso: string): string {
  const момент = parseMoment(iso);
  return момент === null
    ? ''
    : момент.toLocaleString('ru-RU', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}
